import { createContext, useContext, useState, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { isAuthenticated } = useAuth();

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await api.get('/notifications');
      const items = (data.notifications || []).map((n) => ({
        ...n,
        _id: n.id,
        read: !!n.is_read,
        createdAt: n.created_at,
      }));
      setNotifications(items);
      setUnreadCount(items.filter((n) => !n.read).length);
    } catch {
      // Silently fail — notifications are non-critical
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(
    async (notificationId) => {
      try {
        await api.put(`/notifications/${notificationId}/read`);
        setNotifications((prev) =>
          prev.map((n) =>
            n._id === notificationId ? { ...n, read: true } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // Silently fail
      }
    },
    []
  );

  const generateNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      await api.post('/notifications/generate');
      await fetchNotifications();
    } catch {
      // Silently fail
    }
  }, [isAuthenticated, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    generateNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return context;
}

export default NotificationContext;
